import { createTimeInstance } from '@utils/time';

// Centralized human labels + formatters for the Score Spec feature. Every surface that shows a
// status, source, spec id, count, or date routes through here so the same machine value never
// renders two different ways across screens. Mirrors src/features/scoreBackfill/format.js.

const EM_DASH = '—';

// Title-case an unknown snake_case / spaced token so we never echo raw machine strings.
const humanizeToken = (value) =>
  String(value)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

export const STATUS_LABELS = {
  draft: 'Draft',
  locked: 'Locked',
  approved: 'Approved',
  retired: 'Retired',
};

export const SOURCE_LABELS = {
  api: 'Authored',
  seed: 'Seed',
};

export const formatStatus = (status) => STATUS_LABELS[status] || (status ? humanizeToken(status) : EM_DASH);

export const formatSource = (source) => SOURCE_LABELS[source] || (source ? humanizeToken(source) : EM_DASH);

// 'share_buyback_mentions_v1' -> 'Share Buyback Mentions (v1)'
export const formatSpecId = (id) => {
  if (!id) return EM_DASH;
  const parts = String(id)
    .split(/[_\s]+/)
    .filter(Boolean);
  const rendered = parts.map((part) => {
    if (/^v\d+$/i.test(part)) return `(${part.toLowerCase()})`;
    return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
  });
  return rendered.join(' ').replace(/\s+\(/g, ' (');
};

// Prefer an explicit human name if the record ever carries one; otherwise render the
// score_spec_id as a clean title. The description is intentionally NOT used as the label —
// it is sentence-length prose, not a name.
export const specDisplayName = (spec) => {
  if (!spec) return EM_DASH;
  return spec.name || formatSpecId(spec.score_spec_id);
};

// The record carries an integer user id and nothing else — no endpoint maps one to a name — so this
// is as human as an author can be rendered today. Deliberately says nothing about WHICH user is
// reading: `created_by` and `approved_by` share this formatter, and only one of them has a meaning
// for null (a seed spec has no author; an unapproved spec simply has not been approved). The caller
// supplies that word, the same way userLabel's seedLabel parameter used to.
export const formatUser = (id) => (id == null ? EM_DASH : `User #${id}`);

export const formatDate = (d) => (d ? createTimeInstance(d).format('MMM D, YYYY') : EM_DASH);

export const formatDateTime = (d) => (d ? createTimeInstance(d).format('MMM D, YYYY h:mm A') : EM_DASH);

export const formatCount = (n) => (typeof n === 'number' && isFinite(n) ? n.toLocaleString() : EM_DASH);

// Point-in-time values that came off a preview run are engine timestamps, and the operator is
// comparing them against a spec's date_range. `formatDate` above is dayjs in LOCAL time, so a UTC
// instant renders a day early for any US operator — which would make the two timestamps on a run
// row contradict each other. These render UTC and say so. No dayjs utc plugin is loaded in this
// app (utils/time.js extends only advancedFormat + relativeTime), so this goes through Date.
const asUtcParts = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const iso = date.toISOString();
  return { day: iso.slice(0, 10), time: iso.slice(11, 16) };
};

export const formatUtcDate = (value) => {
  const parts = asUtcParts(value);
  return parts ? parts.day : EM_DASH;
};

export const formatUtcDateTime = (value) => {
  const parts = asUtcParts(value);
  return parts ? `${parts.day} ${parts.time} UTC` : EM_DASH;
};

// A resolved window, as the engine echoed it back. Mirrors scoreBackfill/format.js:97, which this
// module has always been a partial copy of (see the header) — consolidating the two is a separate
// job, and duplicating one more formatter to avoid a cross-feature import is the smaller wrong.
export const formatWindow = (from, to) => `${formatUtcDate(from)} → ${formatUtcDate(to)}`;

// Whether a timestamp will actually PRINT as a date. Callers gate a "published at …" line on this
// rather than on truthiness, so an unparseable value cannot render as `published —` — a sentence
// claiming the engine reported something it did not. Shares asUtcParts with the formatters above,
// so the gate and the printer can never disagree.
export const isPrintableUtc = (value) => asUtcParts(value) !== null;
